import { DqCard, DqCardContent } from "@decentriq/components";
import { Alert, Stack, Typography } from "@mui/joy";
import { useMount } from "ahooks";
import { Fragment, memo, useMemo } from "react";
import {
  EstimatedAudienceSize,
  MediaDataRoomVersionSwitch,
  QualityScore,
  ReachTradeoff,
} from "features/mediaDataRoom/components";
import { useMediaDataRoom } from "features/mediaDataRoom/contexts";
import { type LookalikeAudienceStatistics } from "features/mediaDataRoom/hooks";
import { useLookalikeAudienceStatistics as useLookalikeAudienceAbStatistics } from "features/mediaDataRoom/hooks/ab";
import { useLookalikeAudienceStatistics as useLookalikeAudienceV3Statistics } from "features/mediaDataRoom/hooks/v3";
import {
  type AdvertiserAudienceAb,
  ALL_BASE_USERS_AUDIENCE,
  ALL_PUBLISHER_USERS_AUDIENCE,
  type AllUsersAudience,
  type Audience,
  type AudienceAb,
  audienceTypePresentationMap,
  type AudienceV3,
  MediaDataRoomVersion,
  type RuleBasedAudienceAb,
  type RuleBasedAudienceV3,
} from "features/mediaDataRoom/models";
import { useAudiences } from "features/mediaDataRoom/wrappers";
import AudienceCreationDate from "../AudienceCreationDate/AudienceCreationDate";
import AudienceV3Creator from "../AudienceCreator/AudienceV3Creator";
import AudienceName from "../AudienceName/AudienceName";
import AudienceAbStatusLabel from "../AudienceStatusChip/AudienceAbStatusChip";
import AudienceV3StatusChip from "../AudienceStatusChip/AudienceV3StatusChip";
import AudienceAbDrawerRuleBasedDetails from "./AudienceDrawerRuleBasedDetails/AudienceAbDrawerRuleBasedDetails";
import AudienceV3DrawerRuleBasedDetails from "./AudienceDrawerRuleBasedDetails/AudienceV3DrawerRuleBasedDetails";

interface AudienceAbDrawerDetailsProps {
  audience: Audience;
}

interface AudienceV3DrawerDetailsProps {
  audience: AudienceV3;
}

interface AudienceDrawerDetailsWithStatisticsProps {
  audience: Audience;
  seedAudience: Audience | AllUsersAudience | null;
  statistics: LookalikeAudienceStatistics;
  canEstimateSize: boolean;
}

const AudienceDrawerDetails = memo<AudienceDrawerDetailsWithStatisticsProps>(
  (props) => {
    const {
      statistics: {
        audienceQualityScore,
        computeState: { loading: isLookalikeAudienceStatisticsLoading },
      },
      audience,
      seedAudience,
    } = props;
    const { id, kind, name, createdAt } = audience;
    const {
      supportedFeatures: { showAbsoluteValues, canViewAudienceStatus },
    } = useMediaDataRoom();
    const {
      audienceSizes: { enableSizeEstimationForAudience, audienceSizes },
    } = useAudiences();
    useMount(() => {
      if (props.canEstimateSize && showAbsoluteValues) {
        enableSizeEstimationForAudience(id);
      }
    });
    return (
      <Stack gap={1}>
        <DqCard>
          <DqCardContent alignItems="flex-start">
            <Stack spacing={0.5} width="100%">
              <Typography
                level="body-sm"
                startDecorator={<b>Type:</b>}
                textColor="inherit"
              >
                {audienceTypePresentationMap.get(kind) || "N/A"}
              </Typography>
              <MediaDataRoomVersionSwitch
                ab={null}
                v3={
                  <Typography
                    level="body-sm"
                    startDecorator={<b>Created by:</b>}
                    textColor="inherit"
                  >
                    <AudienceV3Creator
                      createdBy={(audience as AudienceV3).createdBy}
                    />
                  </Typography>
                }
              />
              <Typography
                level="body-sm"
                startDecorator={<b>Creation date:</b>}
                textColor="inherit"
              >
                <AudienceCreationDate createdAt={createdAt} />
              </Typography>
              <MediaDataRoomVersionSwitch
                ab={null}
                v3={
                  <Fragment>
                    {kind === "advertiser" && seedAudience ? (
                      <Fragment>
                        <Typography
                          level="body-sm"
                          startDecorator={<b>Audience used as seed:</b>}
                          textColor="inherit"
                        >
                          <AudienceName
                            kind={seedAudience.kind}
                            name={seedAudience.name}
                          />
                        </Typography>
                      </Fragment>
                    ) : null}
                  </Fragment>
                }
              />
              {kind === "lookalike" && seedAudience ? (
                <Fragment>
                  <Typography
                    level="body-sm"
                    startDecorator={<b>Audience used as seed:</b>}
                    textColor="inherit"
                  >
                    <AudienceName
                      kind={seedAudience.kind}
                      name={seedAudience.name}
                    />
                  </Typography>
                  <Typography
                    level="body-sm"
                    startDecorator={<b>Seed audience:</b>}
                    textColor="inherit"
                  >
                    {audience.excludeSeedAudience ? "Excluded" : "Included"}
                  </Typography>
                </Fragment>
              ) : null}
              {canViewAudienceStatus && (
                <Typography
                  display="block"
                  level="body-sm"
                  startDecorator={<b>Status:</b>}
                  textColor="inherit"
                >
                  <MediaDataRoomVersionSwitch
                    ab={<AudienceAbStatusLabel {...(audience as AudienceAb)} />}
                    v3={
                      <AudienceV3StatusChip
                        {...(audience as AudienceV3)}
                        extendedOnError={true}
                      />
                    }
                  />
                </Typography>
              )}
            </Stack>
          </DqCardContent>
        </DqCard>
        {kind === "rulebased" && (
          <MediaDataRoomVersionSwitch
            ab={
              <AudienceAbDrawerRuleBasedDetails
                audience={audience as RuleBasedAudienceAb}
              />
            }
            v3={
              <AudienceV3DrawerRuleBasedDetails
                audience={audience as RuleBasedAudienceV3}
              />
            }
          />
        )}
        {showAbsoluteValues && (
          <MediaDataRoomVersionSwitch
            ab={
              <EstimatedAudienceSize
                audienceSize={
                  (audience as AudienceAb).kind === "advertiser"
                    ? ((audience as AdvertiserAudienceAb).size ?? null)
                    : audienceSizes[id]?.audienceSize
                }
                loading={audienceSizes[id]?.loading}
              />
            }
            v3={
              <EstimatedAudienceSize
                audienceSize={audienceSizes[id]?.audienceSize}
                loading={audienceSizes[id]?.loading}
              />
            }
          />
        )}
        {kind === "lookalike" && (
          <Fragment>
            <ReachTradeoff reach={audience.reach} />
            <QualityScore
              loading={isLookalikeAudienceStatisticsLoading}
              qualityScore={audienceQualityScore}
            />
          </Fragment>
        )}
        <Alert>
          <MediaDataRoomVersionSwitch
            ab={
              <Fragment>
                {kind === "advertiser"
                  ? `This audience has been automatically generated by matching all users from ${name} with the publisher audience.`
                  : "This audience can’t be edited anymore."}
              </Fragment>
            }
            v3={"This audience can’t be edited anymore."}
          />
        </Alert>
      </Stack>
    );
  }
);
AudienceDrawerDetails.displayName = "AudienceDrawerDetails";

const AudienceAbDrawerDetails = memo<AudienceAbDrawerDetailsProps>(
  ({ audience }) => {
    const { getParameterPayloads, audiences } = useAudiences(
      MediaDataRoomVersion.ab
    );
    const statistics = useLookalikeAudienceAbStatistics({
      audienceId: audience.id,
      audienceReach: audience.kind === "lookalike" ? audience.reach : 0,
      getParameterPayloads,
      skip: audience.kind !== "lookalike",
    });
    const seedAudience = useMemo<Audience | AllUsersAudience | null>(() => {
      if (audience.kind === "advertiser") {
        return null;
      }
      return (
        [
          ...(audiences.computeResults ?? []),
          ALL_PUBLISHER_USERS_AUDIENCE,
        ].find((a) => a.id === audience.sourceRef) ?? null
      );
    }, [audience, audiences]);
    return (
      <AudienceDrawerDetails
        audience={audience}
        canEstimateSize={audience.kind !== "advertiser"}
        seedAudience={seedAudience}
        statistics={statistics}
      />
    );
  }
);
AudienceAbDrawerDetails.displayName = "AudienceAbDrawerDetails";

const AudienceV3DrawerDetails = memo<AudienceV3DrawerDetailsProps>(
  ({ audience }) => {
    const { audiences, seedAudiences, getAudienceById } = useAudiences(
      MediaDataRoomVersion.v3
    );
    const statistics = useLookalikeAudienceV3Statistics({
      audienceId: audience.id,
      audienceReach: audience.kind === "lookalike" ? audience.reach : 0,
      audiences: audiences?.computeResults,
      seedAudiences: seedAudiences?.computeResults,
      skip: audience.kind !== "lookalike" || audience.isStale,
    });
    const seedAudience = useMemo<AudienceV3 | AllUsersAudience | null>(() => {
      if (
        audience.id === ALL_BASE_USERS_AUDIENCE.id &&
        audience.kind === ALL_BASE_USERS_AUDIENCE.kind
      ) {
        return ALL_BASE_USERS_AUDIENCE;
      }
      try {
        return getAudienceById(audience.sourceRef, true) as AudienceV3;
      } catch {
        if (audience.sourceRefKind === "seed") {
          const refAsSeed: AllUsersAudience = {
            id: audience.sourceRef,
            kind: "advertiser",
            name: audience.sourceRef,
          };
          return refAsSeed;
        }
        return null;
      }
    }, [
      getAudienceById,
      audience.sourceRef,
      audience.kind,
      audience.id,
      audience.sourceRefKind,
    ]);
    return (
      <AudienceDrawerDetails
        audience={audience}
        canEstimateSize={!audience.isStale}
        seedAudience={seedAudience}
        statistics={statistics}
      />
    );
  }
);
AudienceV3DrawerDetails.displayName = "AudienceV3DrawerDetails";

type AudienceDrawerDetailsProps = AudienceAbDrawerDetailsProps;

const AudienceDrawerDetailsVersioned = memo<AudienceDrawerDetailsProps>(
  (props) => (
    <MediaDataRoomVersionSwitch
      ab={<AudienceAbDrawerDetails {...props} />}
      v3={
        <AudienceV3DrawerDetails {...(props as AudienceV3DrawerDetailsProps)} />
      }
    />
  )
);
AudienceDrawerDetailsVersioned.displayName = "AudienceDrawerDetailsVersioned";

export default AudienceDrawerDetailsVersioned;
